import { useLayoutEffect, useState, type CSSProperties, type RefObject } from 'react';

// Viewport-aware placement for anchored pop-ups.
//
// CLAUDE.md §5: "Pop-ups anchor to the control that opened them. Menus, flyouts and
// pickers open next to their trigger, not centered on the screen."
//
// One rule, one implementation: a panel must never be cut off. It is rendered
// hidden for a frame, measured, then positioned — so it flips above its trigger
// when there is no room below, clamps to the window using its REAL width (a
// declared width cannot account for a panel sized by its own content), and caps
// its height with an internal scroll rather than running past the screen edge.
//
// Adapted from Tomo's `anchoredPanel.ts`. Their version divided every value by a
// global `zoom` factor; we deliberately did not carry that trick over — it taxes
// every pointer calculation in the codebase — so the maths here is plain screen
// pixels throughout.

/** Breathing room between trigger and panel, and between panel and window edge. */
const GAP = 4;
const EDGE = 8;
/** A flipped panel never gets squeezed below this before the other side wins. */
const MIN_USABLE = 120;

export interface AnchorRect {
  readonly top: number;
  readonly bottom: number;
  readonly left: number;
  readonly right: number;
}

/** Read a trigger element's box in the form the placement maths expects. */
export function anchorFrom(element: Element): AnchorRect {
  const rect = element.getBoundingClientRect();
  return { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right };
}

interface Placement {
  readonly top: number;
  readonly left: number;
  readonly maxHeight: number;
}

/**
 * Style for a portaled panel anchored to `anchor`. Returns a rendered-but-hidden
 * style until the panel has been measured, so it is never seen mispositioned.
 *
 * Every value it produces is measured geometry, which is exactly the case inline
 * style exists for — appearance stays in the panel's own stylesheet.
 */
export function useAnchoredPanel(
  anchor: AnchorRect | null,
  panelRef: RefObject<HTMLElement | null>,
  align: 'left' | 'right' = 'left',
  width?: number,
): CSSProperties {
  const [placement, setPlacement] = useState<Placement | null>(null);

  // This hook is the documented exception to set-state-in-effect: synchronising
  // React with an external system. The panel must be in the DOM before it can be
  // measured, and its real size is what decides whether it flips above the trigger,
  // so there is nothing to derive during render — the value does not exist yet.
  useLayoutEffect(() => {
    if (!anchor) {
      // oxlint-disable-next-line react/set-state-in-effect
      setPlacement(null);
      return;
    }
    const element = panelRef.current;
    if (!element) return;

    const place = (): void => {
      const box = element.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let left = align === 'right' ? anchor.right - box.width : anchor.left;
      left = Math.min(Math.max(EDGE, left), Math.max(EDGE, viewportWidth - box.width - EDGE));

      const below = viewportHeight - anchor.bottom - GAP - EDGE;
      const above = anchor.top - GAP - EDGE;

      // Prefer below; flip only when the panel genuinely does not fit and there is
      // more room the other way.
      if (box.height <= below || below >= above) {
        setPlacement({ top: anchor.bottom + GAP, left, maxHeight: Math.max(MIN_USABLE, below) });
        return;
      }
      setPlacement({
        top: Math.max(EDGE, anchor.top - GAP - Math.min(box.height, above)),
        left,
        maxHeight: Math.max(MIN_USABLE, above),
      });
    };

    place();
    // A filtered list grows as it narrows, the window can resize, and the trigger
    // can scroll away underneath the panel.
    const observer = new ResizeObserver(place);
    observer.observe(element);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor, panelRef, align]);

  return {
    position: 'fixed',
    width,
    maxWidth: `calc(100vw - ${EDGE * 2}px)`,
    overflowY: 'auto',
    ...(placement
      ? { top: placement.top, left: placement.left, maxHeight: placement.maxHeight }
      : // First frame: rendered so it can be measured, invisible so it is not seen.
        { top: 0, left: 0, visibility: 'hidden' }),
  };
}
